import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  apiErrorText,
  useListPtoRdDocumentsQuery,
  useGetRdChangeProtocolQuery,
  useListPtoSqQuery,
  useCreatePtoSqMutation,
  useCreateAutoPtoSqFromRdMutation,
  useAnswerPtoSqMutation,
  useCreateRequestMutation,
  type PtoRdDocument,
  type PtoRequest,
  type RdChange,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import { useAgentPermission } from '@entities/user';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { ChangeDocsPanel } from './rdEdits/ChangeDocsPanel';
import { EditJournalPanel } from './rdEdits/EditJournalPanel';
import { qtyText, statusLabel } from './rdEdits/logic';

/** Вкладки экрана: протокол и SQ, документы изменения (№8, Б), журнал правок РД
 *  (№8) — в адресе `?tab=docs|journal`, чтобы на них можно было сослаться. */
type ChangesTab = 'protocol' | 'docs' | 'journal';

const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const TabButton = styled.button<{ $active: boolean }>`
  font: inherit;
  font-size: 13.5px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: 0;
  border-bottom: 2px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  background: none;
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.text.primary)};
  cursor: pointer;
  margin-bottom: -1px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr minmax(300px, 380px);
  gap: ${({ theme }) => theme.spacing(5)};

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(4)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Select = styled.select`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  font: inherit;
`;

const ChangeRow = styled.div`
  display: grid;
  grid-template-columns: 96px 1fr 90px 90px 80px;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;
`;

const SqRow = styled.div`
  padding: ${({ theme }) => theme.spacing(3)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Badge = styled.span<{ $kind: string }>`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  color: #fff;
  background: ${({ $kind }) =>
    $kind === 'added' ? '#27ae60' : $kind === 'removed' ? '#c0392b' : '#e67e22'};
`;

function changeLabel(k: string): string {
  return k === 'added' ? 'Добавлено' : k === 'removed' ? 'Удалено' : 'Δ объём';
}

function fmt(v: number | null): string {
  return v === null || v === undefined ? '—' : String(v);
}

export function PtoChanges() {
  const { projectId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: ChangesTab = tabParam === 'docs' || tabParam === 'journal' ? tabParam : 'protocol';
  const { setActiveProjectId } = useActiveProjectContext();
  const { can_write } = useAgentPermission('pto');
  const { data: docsData } = useListPtoRdDocumentsQuery({ projectId }, { skip: !projectId });
  const documents: PtoRdDocument[] = docsData?.items ?? [];

  const [docId, setDocId] = useState('');
  const { data: protoData, isFetching: protoLoading } = useGetRdChangeProtocolQuery(docId, {
    skip: !docId,
  });
  const protocol = protoData?.protocol;

  const { data: sqData } = useListPtoSqQuery(projectId, { skip: !projectId });
  const [createSq, { isLoading: creatingSq }] = useCreatePtoSqMutation();
  const [createAutoSq, { isLoading: creatingAutoSq }] = useCreateAutoPtoSqFromRdMutation();
  const [answerSq] = useAnswerPtoSqMutation();
  const [createRequest, { isLoading: creatingReq }] = useCreateRequestMutation();
  const sqList: PtoRequest[] = sqData?.items ?? [];

  const [sqSubject, setSqSubject] = useState('');
  const [sqBody, setSqBody] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [autoSqNote, setAutoSqNote] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) setActiveProjectId(projectId);
  }, [projectId, setActiveProjectId]);

  async function onEscalateChange(
    c: RdChange,
    requestType: 'smeta_adjustment' | 'statut_contract_review' | 'mto_supply',
    targetLabel: string,
  ) {
    setError(null);
    setAutoSqNote(null);
    try {
      const res = await createRequest({
        request_type: requestType,
        source_function: 'pto',
        project_id: projectId || undefined,
        title: `Изменение РД (${changeLabel(c.kind)}): ${c.name}`,
        priority: 'normal',
        payload: {
          document_id: docId || null,
          kind: c.kind,
          code: c.code,
          name: c.name,
          old_qty: c.old_qty,
          new_qty: c.new_qty,
          delta: c.delta,
          notes: `Протокол изменений РД: ${c.name} (${fmt(c.old_qty)} → ${fmt(c.new_qty)}, Δ ${fmt(c.delta)})`,
        },
        submit: true,
      }).unwrap();
      setAutoSqNote(`Создана заявка № ${res.request.id.slice(0, 8)} (${targetLabel}).`);
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось отправить заявку по изменению РД.'));
    }
  }

  async function onCreateSq(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!sqSubject.trim()) {
      setError('Укажите тему запроса.');
      return;
    }
    try {
      await createSq({
        projectId,
        subject: sqSubject.trim(),
        body: sqBody.trim() || undefined,
        related_document_id: docId || undefined,
      }).unwrap();
      setSqSubject('');
      setSqBody('');
    } catch {
      setError('Не удалось создать запрос SQ.');
    }
  }

  async function onAutoCreateSq() {
    setError(null);
    setAutoSqNote(null);
    try {
      const res = await createAutoSq({
        projectId,
        document_id: docId || undefined,
      }).unwrap();
      setAutoSqNote(
        `Сформирован запрос SQ «${res.request.subject}» (пунктов замечаний входного контроля РД: ${res.issues_count}).`,
      );
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось автоматически сформировать запрос SQ по замечаниям РД.'));
    }
  }

  async function onAnswer(id: string) {
    const text = (answers[id] ?? '').trim();
    if (!text) return;
    try {
      await answerSq({ id, answer: text }).unwrap();
      setAnswers((prev) => ({ ...prev, [id]: '' }));
    } catch {
      setError('Не удалось сохранить ответ.');
    }
  }

  return (
    <div>
      <Row>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Изменения РД и запросы SQ</h1>
          <Muted>Протокол изменений между ревизиями · входной контроль РД → запросы SQ в проектный институт</Muted>
        </div>
        <Link to={`/agents/pto/${projectId}`}>
          <Button $variant="secondary">← К проекту</Button>
        </Link>
      </Row>

      <TabBar role="tablist">
        {(
          [
            ['protocol', 'Протокол и запросы SQ'],
            ['docs', 'Документы изменения'],
            ['journal', 'Журнал правок РД'],
          ] as const
        ).map(([key, label]) => (
          <TabButton
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            $active={tab === key}
            onClick={() => {
              const next = new URLSearchParams(params);
              if (key === 'protocol') next.delete('tab');
              else next.set('tab', key);
              setParams(next, { replace: true });
            }}
          >
            {label}
          </TabButton>
        ))}
      </TabBar>

      {tab === 'docs' && <ChangeDocsPanel projectId={projectId} documents={documents} />}
      {tab === 'journal' && <EditJournalPanel projectId={projectId} documents={documents} />}

      {/* Протокол и SQ — скрыт на других вкладках (состояние форм сохраняется). */}
      <Columns style={tab === 'protocol' ? undefined : { display: 'none' }}>
        <div>
          <h3 style={{ marginTop: 0 }}>Протокол изменений РД</h3>
          <Field>
            Документ РД
            <Select value={docId} onChange={(e) => setDocId(e.target.value)}>
              <option value="">— выберите документ —</option>
              {documents.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.code} · {d.title}
                </option>
              ))}
            </Select>
          </Field>

          {!docId ? (
            <Muted>Выберите документ, чтобы сравнить действующую ревизию с предыдущей.</Muted>
          ) : protoLoading ? (
            <Muted>Формирование протокола…</Muted>
          ) : !protocol ? (
            <Muted>Нет данных.</Muted>
          ) : protocol.note ? (
            <Panel>
              <Muted>{protocol.note}</Muted>
            </Panel>
          ) : (
            <>
              <Muted style={{ marginBottom: 8 }}>
                Ревизия {protocol.previous_revision_no} → {protocol.active_revision_no} · добавлено{' '}
                {protocol.added_count} · удалено {protocol.removed_count} · Δ объём{' '}
                {protocol.qty_changed_count}
              </Muted>
              {protocol.changes.length === 0 ? (
                <Panel>
                  <Muted>Изменений объёмов между ревизиями не обнаружено.</Muted>
                </Panel>
              ) : (
                <Panel>
                  {protocol.changes.map((c: RdChange, i) => (
                    <div key={i} style={{ borderBottom: '1px solid rgba(128,128,128,0.18)', padding: '8px 0' }}>
                      <ChangeRow style={{ borderBottom: 'none', padding: 0 }}>
                        <Badge $kind={c.kind}>{changeLabel(c.kind)}</Badge>
                        <span>
                          {c.name}
                          {c.code ? ` · ${c.code}` : ''}
                        </span>
                        <span style={{ textAlign: 'right' }}>{fmt(c.old_qty)}</span>
                        <span style={{ textAlign: 'right' }}>{fmt(c.new_qty)}</span>
                        <span style={{ textAlign: 'right' }}>{fmt(c.delta)}</span>
                      </ChangeRow>
                      {can_write && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                          <Button
                            $variant="ghost"
                            $size="sm"
                            disabled={creatingReq}
                            onClick={() => void onEscalateChange(c, 'smeta_adjustment', 'Сметный отдел')}
                          >
                            → В Сметный
                          </Button>
                          <Button
                            $variant="ghost"
                            $size="sm"
                            disabled={creatingReq}
                            onClick={() => void onEscalateChange(c, 'mto_supply', 'Снабжение')}
                          >
                            → В МТО
                          </Button>
                          <Button
                            $variant="ghost"
                            $size="sm"
                            disabled={creatingReq}
                            onClick={() => void onEscalateChange(c, 'statut_contract_review', 'Юрслужба')}
                          >
                            → В Юрслужбу (ДС)
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </Panel>
              )}

              {protocol.affected_acts.length > 0 && (
                <Panel style={{ marginTop: 12 }}>
                  <Muted style={{ marginBottom: 8, color: '#c0392b', fontWeight: 600 }}>
                    Затронуты подписанные акты — проверьте актуальность (П4):
                  </Muted>
                  {protocol.affected_acts.map((a) => (
                    <SqRow key={a.id}>
                      <div style={{ fontWeight: 600 }}>
                        {a.kind.toUpperCase()} {a.act_no ?? ''} · {a.status}
                      </div>
                      <Muted style={{ fontSize: 12 }}>{a.work_name}</Muted>
                      <Muted style={{ fontSize: 12 }}>Позиции: {a.matched.join(', ')}</Muted>
                    </SqRow>
                  ))}
                </Panel>
              )}
            </>
          )}

          {/* Позиции по документам изменения РД (№8, Б) — вне сравнения ревизий. */}
          {docId && protocol && (protocol.change_doc_positions?.length ?? 0) > 0 && (
            <Panel style={{ marginTop: 12 }}>
              <Muted style={{ marginBottom: 8, fontWeight: 600 }}>
                Позиции по документам изменения (вне сравнения ревизий):
              </Muted>
              {protocol.change_doc_positions?.map((d) => (
                <SqRow key={d.position_id}>
                  <div>
                    <span style={{ fontWeight: 600 }}>{d.name}</span> · {qtyText(d.quantity, d.unit)} ·
                    ревизия {d.revision_no} · {statusLabel(d.status)}
                  </div>
                  {d.basis && <Muted style={{ fontSize: 12 }}>{d.basis}</Muted>}
                </SqRow>
              ))}
              <Link to="?tab=docs">Документы изменения →</Link>
            </Panel>
          )}
        </div>

        <div>
          <h3 style={{ marginTop: 0 }}>Запросы SQ</h3>
          {can_write && (
            <Panel as="form" onSubmit={onCreateSq} style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 12 }}>
                <Button
                  $variant="secondary"
                  $size="sm"
                  type="button"
                  disabled={creatingAutoSq}
                  onClick={() => void onAutoCreateSq()}
                >
                  {creatingAutoSq
                    ? 'Сбор замечаний РД…'
                    : docId
                      ? '⚡ Сформировать SQ по замечаниям выбранного комплекта РД'
                      : '⚡ Сформировать SQ по замечаниям входного контроля РД'}
                </Button>
                {autoSqNote && (
                  <Muted style={{ fontSize: 12, marginTop: 6 }}>{autoSqNote}</Muted>
                )}
              </div>
              <Field>
                Тема запроса
                <Input
                  value={sqSubject}
                  onChange={(e) => setSqSubject(e.target.value)}
                  placeholder="Уточнение узла КЖ-3"
                />
              </Field>
              <Field>
                Текст (опц.)
                <Input
                  value={sqBody}
                  onChange={(e) => setSqBody(e.target.value)}
                  placeholder="Описание вопроса к проектному институту"
                />
              </Field>
              <Button $variant="primary" type="submit" disabled={creatingSq || !sqSubject.trim()}>
                {creatingSq ? 'Создание…' : 'Создать запрос'}
              </Button>
              {docId && <Muted style={{ marginTop: 6, fontSize: 12 }}>Привязка к выбранному документу.</Muted>}
              {error && <Muted style={{ color: 'crimson', marginTop: 8 }}>{error}</Muted>}
            </Panel>
          )}

          {sqList.length === 0 ? (
            <Muted>Запросов SQ пока нет.</Muted>
          ) : (
            <Panel>
              {sqList.map((q) => (
                <SqRow key={q.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontWeight: 600 }}>{q.subject}</span>
                    <Muted style={{ fontSize: 12 }}>
                      {q.status === 'closed' ? 'отвечён' : q.status}
                    </Muted>
                  </div>
                  {q.body && <Muted style={{ fontSize: 13, marginTop: 2 }}>{q.body}</Muted>}
                  {q.raw?.answer ? (
                    <Muted style={{ fontSize: 13, marginTop: 6, color: '#27ae60' }}>
                      Ответ: {q.raw.answer}
                    </Muted>
                  ) : (
                    can_write && (
                      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                        <Input
                          value={answers[q.id] ?? ''}
                          onChange={(e) =>
                            setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))
                          }
                          placeholder="Ответ института…"
                        />
                        <Button
                          $variant="ghost"
                          $size="sm"
                          onClick={() => onAnswer(q.id)}
                          disabled={!(answers[q.id] ?? '').trim()}
                        >
                          Ответить
                        </Button>
                      </div>
                    )
                  )}
                </SqRow>
              ))}
            </Panel>
          )}
        </div>
      </Columns>
    </div>
  );
}
